<template>
  <section
    class="relative p-5 sm:p-10"
    role="dialog"
    aria-modal="true"
    :aria-labelledby="headingId"
    :aria-describedby="descriptionId"
  >
    <u-button
      aria-label="Close more ways to upload modal"
      color="gray"
      variant="ghost"
      icon="i-ri-close-line"
      class="absolute right-4 top-4 z-10"
      @click="modal.close()"
    />

    <div class="mx-auto max-w-6xl">
      <div class="text-center">
        <h1 :id="headingId" class="font-serif text-3xl text-slate-900 sm:text-4xl">
          More Ways to Upload
        </h1>
        <p :id="descriptionId" class="mx-auto mt-2 max-w-4xl text-xl text-charcoal/85">
          FOREVER makes it easy to keep your memories safe—whether you're backing them up as you go or bringing them all together at once.
        </p>
      </div>

      <div class="relative mt-8 grid grid-cols-1 gap-5 lg:grid-cols-2 lg:gap-6">

        <article class="flex h-full flex-col rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <div class="relative mb-6 flex min-h-64 items-center justify-center overflow-hidden rounded-2xl bg-slate-50 ring-1 ring-slate-100">
            <div aria-hidden="true" class="absolute h-72 w-72 rounded-full bg-sky-100" />
            <img
              :src="appsMobileImage"
              alt="FOREVER mobile apps"
              class="pointer-events-none relative z-10 mx-auto w-auto max-w-80 select-none object-contain"
            >
          </div>

          <h3 class="text-center text-xl font-semibold text-primary sm:text-2xl">
            Upload From Your Phone
          </h3>

          <p class="mx-auto mt-3 text-center text-base text-slate-700">
            Back up your photos automatically as you take them, so everything stays organized, easy to find, and always within reach. Access and share your memories anytime, from anywhere—without having to think about it.
          </p>

          <div class="mt-auto pt-6">
            <div class="mx-auto grid w-full max-w-sm grid-cols-2 gap-3">
              <a
                :href="IOS_APP_STORE_URL"
                target="_blank"
                rel="noopener noreferrer"
                class="mx-auto block w-full max-w-40 cursor-pointer transition hover:opacity-90"
              >
                <img
                  :src="appStoreBadge"
                  alt="Download on the App Store"
                  class="w-full"
                >
              </a>

              <a
                :href="ANDROID_PLAY_STORE_URL"
                target="_blank"
                rel="noopener noreferrer"
                class="mx-auto block w-full max-w-40 cursor-pointer transition hover:opacity-90"
              >
                <img
                  :src="googlePlayBadge"
                  alt="Get it on Google Play"
                  class="w-full"
                >
              </a>
            </div>
          </div>
        </article>

        <article class="flex h-full flex-col rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <div class="relative mb-6 flex min-h-64 items-center justify-center overflow-hidden rounded-2xl bg-slate-50 ring-1 ring-slate-100">
            <div aria-hidden="true" class="absolute h-72 w-72 rounded-full bg-violet-100" />
            <img
              :src="appsValetImage"
              alt="FOREVER Valet desktop uploader"
              class="pointer-events-none relative z-10 mx-auto max-h-80 w-auto select-none object-contain"
            >
          </div>

          <h3 class="text-center text-xl font-semibold text-primary sm:text-2xl">
            Upload Your Entire Collection
          </h3>

          <p class="mx-auto mt-3 text-center text-base text-slate-700">
            Bring years of photos and videos into FOREVER Valet quickly and all at once—even thousands of files in a single upload. Keep your existing folders and organization intact with reliable background uploads and automatic retry support.
          </p>

          <div class="mt-auto pt-6">
            <div class="mx-auto max-w-sm">
              <div class="mb-2 flex items-center justify-center gap-3 text-sm text-slate-700">
                <span>Available for Mac &amp; Windows</span>
                <u-icon name="i-ri-apple-fill" class="h-5 w-5 text-slate-800" />
                <u-icon name="i-ri-windows-fill" class="h-5 w-5 text-slate-800" />
              </div>
              <u-button
                color="primary"
                size="xl"
                block
                class="justify-center no-underline"
                :to="valetDownloadUrl"
                external
              >
                Download Valet Desktop Uploader
              </u-button>


            </div>
          </div>
        </article>
      </div>

      <div class="mt-6 flex items-center justify-center gap-3 text-center text-base text-slate-700">
        <u-icon name="i-ri-shield-check-line" class="h-8 w-8 text-primary" />
        <p class="font-semibold">Your memories are always safe with FOREVER.</p>
      </div>
    </div>
  </section>
</template>

<script setup>
  import appsMobileImage from '@/assets/images/apps-mobile.png';
  import appsValetImage from '@/assets/images/apps-valet.png';
  import appStoreBadge from '@/assets/images/appstore.png';
  import googlePlayBadge from '@/assets/images/gplay.png';
  import {EXTERNAL_URLS} from '~/constants/external-urls.js';

  defineProps({
    modal: {
      type: Object,
      required: true
    }
  });

  const headingId = 'more-ways-to-upload-title';
  const descriptionId = 'more-ways-to-upload-description';

  const IOS_APP_STORE_URL = EXTERNAL_URLS.mobileAppIos;
  const ANDROID_PLAY_STORE_URL = EXTERNAL_URLS.mobileAppAndroid;
  const valetDownloadUrl = EXTERNAL_URLS.valet;
</script>
